import type { FC } from 'react';
import { useCallback, useState } from 'react';
import type { DirectDebitStatus } from '@pflegenavi/shared/api';
import { useTranslation } from 'react-i18next';
import { useDeleteDirectDebitMandate } from '@pflegenavi/frontend/api-nursing-home';
import { useFMContext } from '@pflegenavi/frontend/family-member-context';
import { useSnackbar } from 'notistack';
import {
  CancelDirectDebitView,
  CancelModalView,
} from './CancelDirectDebitView';

interface CancelDirectDebitProps {
  status: DirectDebitStatus;
}

export const CancelDirectDebit: FC<CancelDirectDebitProps> = ({ status }) => {
  const [openModal, setOpenModal] = useState(false);
  const handleModalOpen = () => setOpenModal(true);
  const handleModalClose = () => setOpenModal(false);

  return (
    <>
      <CancelDirectDebitView status={status} onCancelClick={handleModalOpen} />
      <CancelModal open={openModal} handleClose={handleModalClose} />
    </>
  );
};

const CancelModal = ({
  open,
  handleClose,
}: {
  open: boolean;
  handleClose: () => void;
}) => {
  const [msg, setMsg] = useState('');
  const { selectedResidentId } = useFMContext();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const onDeleteMandateSuccess = useCallback(() => {
    enqueueSnackbar(t('mobile.family-member.mandate-deletion-success'), {
      variant: 'success',
    });
    handleClose();
  }, [enqueueSnackbar, handleClose, t]);

  const onDeleteMandateError = useCallback(() => {
    enqueueSnackbar(t('mobile.family-member.mandate-deletion-failure'), {
      variant: 'error',
    });
  }, [enqueueSnackbar, t]);

  const {
    mutateAsync: handleDirectDebitCancel,
    isPending,
    isSuccess,
  } = useDeleteDirectDebitMandate(
    selectedResidentId ?? '',
    onDeleteMandateSuccess,
    onDeleteMandateError
  );

  const handleSubmitRequest = async () => {
    if (selectedResidentId) {
      await handleDirectDebitCancel({ residentId: selectedResidentId });
    }
  };

  return (
    <CancelModalView
      open={open}
      handleClose={handleClose}
      message={msg}
      onMessageChange={setMsg}
      onSubmit={handleSubmitRequest}
      isPending={isPending}
      isSuccess={isSuccess}
    />
  );
};
